import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import SimpleNotes from "../../components/notes/SimpleNotes";
import AttachmentsCard from "../AttachmentsCard";
import CostCurrencySelect from "../CostCurrencySelect";
import type { UpdateCostRequest } from "../costApi";
import type { CostDetails } from "../costTypes";
import CostStatusSelect from "./CostStatusSelect";
import InvoicesCard from "./InvoicesCard";
import OrderCard from "./OrderCard";
import ProviderCard from "./ProviderCard";
import { useCost } from "./useCost";

type CostProperties = Pick<CostDetails, "currency" | "status" | "invoices">;

interface CostCardProps {
  existingCost: CostProperties;
  onUpdate: (value: UpdateCostRequest) => Promise<void>;
}

const CostCard: FC<CostCardProps> = ({ existingCost, onUpdate }) => {
  const [cost, setCost] = useState<CostProperties>({
    currency: existingCost.currency,
    status: existingCost.status,
    invoices: existingCost.invoices,
  });
  useEffect(() => {
    setCost((cost) => ({ ...cost, currency: existingCost.currency }));
  }, [existingCost.currency]);

  const handleChange = (change: Partial<CostProperties>) => {
    setCost((c) => ({ ...c, ...change }));
  };

  const handleSubmit = async () => {
    await onUpdate({ status: cost.status, currency: cost.currency });
  };

  return (
    <Card>
      <CardHeader title="Koszt" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={6}>
            <CostStatusSelect status={cost.status} onChange={(status) => handleChange({ status })} />
          </Grid>
          <Grid size={3}>
            <CostCurrencySelect value={cost.currency} onChange={(currency) => currency && handleChange({ currency })} />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <CancelButton onClick={() => setCost(existingCost)} />
          <AsyncLoadingButton onClick={handleSubmit}>Zapisz</AsyncLoadingButton>
        </Stack>
      </CardActions>
    </Card>
  );
};

const EditCostPage = () => {
  const navigate = useNavigate();
  const {
    cost,
    isLoading,
    updateCost,
    deleteCost,
    addInvoice,
    updateInvoiceStatus,
    deleteInvoice,
    updateInvoicesExchangeRate,
    addAttachment,
    deleteAttachment,
    updateNotes,
  } = useCost();
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);

  const navigateToCostList = () => navigate("/costs");

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!cost) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Taki koszt nie istnieje</Typography>
      </Grid>
    );
  }

  const handleDelete = async () => {
    await deleteCost();
    setConfirmationDialogVisible(false);
    navigateToCostList();
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid container size={12} spacing={2}>
        <Grid size={12}>
          <Typography variant="h4">Rezerwacja {cost.bookingNumber}</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <OrderCard date={cost.date} order={cost.order} />
        </Grid>
        <Grid container size={{ xs: 12, lg: 6 }} spacing={2}>
          <Grid size={12}>
            <ProviderCard providerId={cost.provider.id} bookingNumber={cost.bookingNumber} order={cost.order} />
          </Grid>
          <Grid size={12}>
            <CostCard
              existingCost={cost}
              onUpdate={async (u) => {
                await updateCost(u);
              }}
            />
          </Grid>
        </Grid>
        <Grid size={12}>
          <InvoicesCard
            cost={cost}
            onAddInvoice={addInvoice}
            onUpdateInvoiceStatus={updateInvoiceStatus}
            onDeleteInvoice={deleteInvoice}
            onUpdateInvoicesExchangeRate={async () => {
              await updateInvoicesExchangeRate();
            }}
            onChangeCostCurrency={async (currency) => {
              await updateCost({ currency });
            }}
          />
        </Grid>
        <Grid size={12}>
          <AttachmentsCard
            files={cost.files}
            onAddAttachment={async (f) => {
              await addAttachment(f);
            }}
            onDeleteAttachment={async (name) => {
              await deleteAttachment(name);
            }}
          />
        </Grid>
        <Grid size={12}>
          <Card>
            <CardHeader title="Notatki" />
            <CardContent>
              <SimpleNotes
                notes={cost.notes ?? []}
                onChange={async (notes) => {
                  await updateNotes(notes);
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" color="secondary" onClick={navigateToCostList}>
            Lista kosztów
          </Button>
          <Button variant="outlined" color="error" onClick={() => setConfirmationDialogVisible(true)}>
            Usuń
          </Button>
          <ConfirmationDialog
            open={confirmationDialogVisible}
            title="Usuń zamówienie"
            content="Czy chcesz usunąć szczegóły kosztu? Status, numer faktury oraz kwota do zapłaty zostaną usunięte. Tej operacji nie będzie można już cofnąć."
            onCancel={() => setConfirmationDialogVisible(false)}
            onConfirm={handleDelete}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default EditCostPage;
